import { FC, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ISelectOptionProps, IFileDetails } from 'components/form/types';
import Form from 'components/form/Form';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import styled from 'styled-components';
import { QueryClientProvider, useQueryClient } from 'react-query';
import { formatDate } from 'utils/helpers/formatters';
import FormGroup from 'components/form/FormGroup';
import CreateEntity from 'components/crud/CreateEntity';
import Col from 'react-bootstrap/Col';
import { useModal } from 'store/modal';
import Row from 'react-bootstrap/Row';

import { useTranslation } from 'react-i18next';
import { getUserInfo } from 'utils/auth';

interface IInputs {
  type: string;
  sub_type: string;
  date_to: Date;
  user_id: number | null;
  substitute_employee_id?: ISelectOptionProps;
}

interface IProps {
  title: string;
  dropdowns?: {
    user?: ISelectOptionProps[];
    types?: ISelectOptionProps[];
    paidSubTypes?: ISelectOptionProps[];
    additionalSubTypes?: ISelectOptionProps[];
    substituteEmployees?: ISelectOptionProps[];
  };
}

const ManuallyInsert: FC<IProps> = ({ title, dropdowns }: IProps) => {
  const methods = useForm<IInputs>({
    defaultValues: {
      type: '',
      sub_type: '',
      user_id: null,
    },
  });
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { watch, setValue } = methods;
  const [isFileUploading, setIsFileUploading] = useState(false);
  const { showModal } = useModal();
  const [startDate, setStartDate] = useState(new Date());
  const [resetForm, setResetForm] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<IFileDetails[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState<string[]>([]);
  const [holidaysCache, setHolidaysCache] = useState<{ [year: number]: string[] }>({});
  const queryClient = useQueryClient();

  useEffect(() => {
    // Fetch the holidays for the selected year
    const fetchHolidays = async () => {
      // If holidays for the year are already fetched, skip fetching
      if (holidaysCache[selectedYear]) {
        return;
      }

      try {
        const response = await API.get(`holidays/${selectedYear}`);
        const holidayDates = response.data.items.map((item: any) => item.date);
        // Update the cache with the newly fetched holidays
        setHolidaysCache(prevCache => ({
          ...prevCache,
          [selectedYear]: holidayDates,
        }));
      } catch (error) {
        showModal({
          type: 'error',
          content: createErrorFromResponse(error).message,
        });
      }
    };

    fetchHolidays();
  }, [selectedYear, holidaysCache]);

  const onFilesUploaded = (fl: IFileDetails[]) => {
    setUploadedFiles([...uploadedFiles, ...fl]);
  };

  const onFilesDeleted = (fl: any) => {
    setUploadedFiles(current => current.filter(file => fl.name !== file.name));
  };

  const leaveTypes: { [key: string]: string } = {
    sick: 'sick',
    paid: 'paid',
    unpaid: 'unpaid',
    additional: 'additional',
  };
  const fieldType = watch('type');

  const isHoliday = (date: Date) => {
    const day = date.getDay();
    const formattedDate = formatDate(date, 'YYY-mm-dd');
    const holidaysForYear = holidaysCache[date.getFullYear()] || [];
    // Return true if it's not a weekend and not a holiday, false otherwise
    return !(day === 0 || day === 6 || holidaysForYear.includes(formattedDate));
  };

  // @ts-ignore
  const fieldTypeValue = fieldType?.value;

  const isSickLeave = fieldTypeValue === 'sick';

  const currentSubTypes = fieldTypeValue === leaveTypes.paid ? dropdowns?.paidSubTypes : dropdowns?.additionalSubTypes;
  const showSubType = fieldTypeValue === leaveTypes.paid || fieldTypeValue === leaveTypes.additional;

  const userInfo = getUserInfo();
  const isRequireLeaveSubstituteEmployee = userInfo?.featureRequireLeaveSubstituteEmployee;
  const showSubstituteDropdown =
    userInfo?.featureLeaveSubstituteEmployee &&
    (fieldTypeValue === leaveTypes.paid || fieldTypeValue === leaveTypes.unpaid);

  useEffect(() => {
    if (!showSubstituteDropdown) {
      setValue('substitute_employee_id', undefined);
    }
  }, [showSubstituteDropdown, setValue]);

  return (
    <StyledFormGroup>
      <Row>
        <Col className="col-sm-6">
          <QueryClientProvider client={queryClient}>
            <CreateEntity
              entity="leaves/manually"
              methods={methods}
              queryKey={buildClientEntity('employer-leaves', client)}
              onSuccess={(res: any) => {
                setResetForm(true);
                showModal({
                  type: 'success',
                  content: <>{t('leaves.manuallyInsertedLeave')}</>,
                });
                setResetForm(false);
                setUploadedFiles([]);
                methods.reset();
              }}
            >
              {(create: any) => (
                <Form
                  title={title}
                  methods={methods}
                  btnLabel={t('leaves.manuallyInsertLeaveCreate')}
                  primaryActionBtnType="primary"
                  isUploading={isFileUploading}
                  customError
                  onSubmit={(data: any) => {
                    showModal({
                      type: 'confirm',
                      heading: t('common.confirm'),
                      content: t('leaves.manuallyInsertLeaveCreateConfirmation'),
                      onConfirm: async () => {
                        try {
                          const formatData = {
                            user_id: data?.user_id.value,
                            type: data?.type.value,
                            date_from: formatDate(data.date_from, 'YYY-mm-dd'),
                            date_to: formatDate(data.date_to, 'YYY-mm-dd'),
                            attachments: uploadedFiles.map(file => file.id),
                            take_paid_days: data.take_paid_days && fieldTypeValue === leaveTypes.paid ? 1 : 0,
                            request_document_id: data?.request_document_id,
                            order_document_id: data?.order_document_id ?? null,
                            comment: data.comment || undefined,
                            substitute_employee_id: data?.substitute_employee_id?.value || undefined,
                            ...(data.type.value === 'paid' || data.type.value === 'additional'
                              ? {
                                  sub_type: data?.sub_type.value ? data?.sub_type.value : 'paid_ordinary',
                                }
                              : ''),
                          };

                          if (!data.request_document_id) {
                            formatData.request_document_id = data?.request_document_id;
                            delete formatData.request_document_id;
                          }

                          if (!data.order_document_id) {
                            delete formatData.order_document_id;
                          }

                          await create(formatData);
                        } catch (error: any) {
                          showModal({
                            type: 'error',
                            content: <>{error.data.errors_common}</>,
                          });
                        }
                      },
                      confirmLabel: t('common.confirm'),
                    });
                  }}
                >
                  <FormGroup
                    controlId="formGroupUsers"
                    label={t('docWizard.step1.docEmployee')}
                    type="select"
                    as="select"
                    name="user_id"
                    isRequired
                    selectOptions={dropdowns?.user}
                  />
                  <Row className="mt-1">
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupType"
                        label={t('leaves.type')}
                        type="select"
                        name="type"
                        selectOptions={dropdowns?.types}
                        isRequired
                      />
                    </Col>

                    <Col
                      md={{ span: 6, offset: 0 }}
                      hidden={!showSubType}
                    >
                      <FormGroup
                        controlId="formGroupSubType"
                        label={t('leaves.subType')}
                        type="select"
                        name="sub_type"
                        selectOptions={currentSubTypes}
                        isRequired={showSubType}
                      />
                    </Col>
                  </Row>
                  {showSubstituteDropdown && (
                    <Row>
                      <Col md={{ span: 6, offset: 0 }}>
                        <FormGroup
                          controlId="formGroupSubstituteEmployee"
                          label={t('leaves.substituteEmployeeManual')}
                          type="select"
                          name="substitute_employee_id"
                          selectOptions={dropdowns?.substituteEmployees}
                          isRequired={isRequireLeaveSubstituteEmployee}
                          isSearchable
                          isClearable
                          placeholder={t('form.select')}
                        />
                      </Col>
                    </Row>
                  )}
                  {fieldTypeValue === leaveTypes.paid && (
                    <Row>
                      <Col>
                        <FormGroup
                          controlId="formGroupTakePaidLeaveDays"
                          label=""
                          checkType="checkbox"
                          name="take_paid_days"
                          isHorizontal
                          options={[
                            {
                              label: t('leaves.takePaidLeaveDays'),
                              name: 'take_paid_days',
                              id: 'take_paid_days',
                              value: 'true',
                            },
                          ]}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className="mt-1">
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupDateFrom"
                        label={t('leaves.dateFrom')}
                        onChange={date => {
                          methods.clearErrors();
                          setStartDate(date);
                          setSelectedYear(date.getFullYear());
                        }}
                        type="datepicker"
                        name="date_from"
                        filterDate={isSickLeave ? undefined : isHoliday}
                        dateFormat="dd.MM.yyyy"
                        showYearDropdown
                        showMonthDropdown
                        isRequired
                      />
                    </Col>
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupDateTo"
                        label={t('leaves.dateTo')}
                        type="datepicker"
                        minDate={startDate}
                        name="date_to"
                        onChange={date => {
                          methods.clearErrors();
                          setSelectedYear(date.getFullYear());
                        }}
                        filterDate={isSickLeave ? undefined : isHoliday}
                        dateFormat="dd.MM.yyyy"
                        showYearDropdown
                        showMonthDropdown
                        isRequired
                      />
                    </Col>
                  </Row>
                  <Row className="mt-1">
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupRequestId"
                        label={t('leaves.requestDocumentId')}
                        type="number"
                        name="request_document_id"
                        hint={t('leaves.requestDocumentComment')}
                      />
                    </Col>
                    <Col md={{ span: 6, offset: 0 }}>
                      <FormGroup
                        controlId="formGroupRequestId"
                        label={t('leaves.orderDocumentId')}
                        type="number"
                        name="order_document_id"
                        hint={t('leaves.orderDocumentComment')}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormGroup
                        controlId="formGroupComment"
                        label={t('leaves.comment')}
                        type="text"
                        name="comment"
                        maxLength={255}
                        hint={t('leaves.commentInfoManual')}
                      />
                    </Col>
                  </Row>

                  <>
                    <hr />
                    <h4 className="text-center mb-4">{t('docWizard.step3.attachDocuments')}</h4>
                    <FormGroup
                      controlId="formGroupFileUpload"
                      type="file"
                      placeholder={t('fileUploader.placeholder')}
                      name="file"
                      accept={['application/pdf', '.jpg', '.png', '.jpeg']}
                      multiple
                      uploadUri="leaves/upload-attachment"
                      deleteFileUri="leaves/attachments"
                      hint={t('fileUploader.hint', { accept: 'pdf, jpg, png, jpeg' })}
                      isFileUploading={(isUploading: boolean) => {
                        setIsFileUploading(isUploading);
                      }}
                      onFilesUploaded={onFilesUploaded}
                      reset={resetForm}
                      onFilesDeleted={onFilesDeleted}
                    />
                  </>
                </Form>
              )}
            </CreateEntity>
          </QueryClientProvider>
        </Col>
      </Row>
    </StyledFormGroup>
  );
};

export default ManuallyInsert;

const StyledFormGroup = styled.div`
  .react-datepicker__day--weekend {
    color: #d1d1d1; /* Gray color for weekends */
  }
`;
